import type {
  ZodArray,
  ZodObject,
  ZodRawShape,
  ZodReadonly,
  ZodRecord,
  ZodString,
  ZodType,
} from "zod";
import { z } from "zod";

import type { FieldDescriptionsArgs } from "./field-descriptions.ts";
import { FIELD_DESCRIPTIONS, describeShape } from "./field-descriptions.ts";
import type { ReadonlyRecord, UUID } from "./shared.ts";

const ALPHA_NUMERIC_REGEXP = /^[A-Za-z0-9]+$/u;

const SAFE_FILE_NAME_REGEXP = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*$/u;

export function alphaNumeric(): ZodString {
  return z
    .string()
    .regex(ALPHA_NUMERIC_REGEXP, "Must contain only letters and digits");
}

export function array<T extends ZodType>(schema: T): ZodReadonly<ZodArray<T>> {
  return z.array(schema).readonly();
}

export function object<T extends ZodRawShape>(
  shape: T,
  ...[descriptions]: FieldDescriptionsArgs<T>
): ZodReadonly<ZodObject<T>> {
  const fieldDescriptions: ReadonlyRecord<string, string> = descriptions ?? {};

  const schema = z.object(describeShape(shape, fieldDescriptions)).readonly();

  FIELD_DESCRIPTIONS.set(schema, fieldDescriptions);

  return schema;
}

export function record<T extends ZodType>(
  schema: T,
): ZodReadonly<ZodRecord<ZodString, T>>;
export function record<
  K extends ZodType<PropertyKey, PropertyKey>,
  T extends ZodType,
>(keySchema: K, schema: T): ZodReadonly<ZodRecord<K, T>>;
export function record(
  ...args:
    | readonly [keySchema: ZodType<PropertyKey, PropertyKey>, schema: ZodType]
    | readonly [schema: ZodType]
): ZodReadonly<ZodRecord<ZodType<PropertyKey, PropertyKey>, ZodType>> {
  if (args.length === 1) {
    return z.record(z.string(), args[0]).readonly();
  }

  const [keySchema, schema] = args;

  return z.record(keySchema, schema).readonly();
}

export function regexp(): ZodString {
  return z
    .string()
    .min(1)
    .superRefine((value, ctx) => {
      try {
        new RegExp(value, "u");
      } catch (error) {
        ctx.addIssue({
          code: "custom",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    });
}

export function safeFileName(): ZodString {
  return z.string().regex(SAFE_FILE_NAME_REGEXP, "Must be a safe file name");
}

export function uuid(): ZodType<UUID, string> {
  // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
  return z.uuid().transform(value => value as UUID);
}
